import { Component, forwardRef, input, model, signal, ChangeDetectionStrategy } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { BaseControlValueAccessor } from "../reactive-forms-control-value-accessor/base.control.value.accessor.directive";
import { FormValueControl } from "@angular/forms/signals";

@TypeManagerDecorator("s25-ng-radio")
@Component({
    selector: "s25-ng-radio",
    template: `
        <div class="d-inline-block">
            <input
                type="radio"
                [id]="'radio-button-' + id()"
                class="c-label--input"
                [(ngModel)]="value"
                (ngModelChange)="onChange($event)"
                [name]="radioGroupName() || name()"
                [value]="radioValue()"
                [disabled]="disabled()"
                [attr.aria-label]="ariaLabel() + '-' + id()"
            />
            <label [attr.for]="'radio-button-' + id()" class="c-label--radio">
                <div class="bubble"></div>
                <ng-content></ng-content>
            </label>
        </div>
    `,
    styles: `
        .c-label--radio {
            align-items: center;
            display: inline-flex;
            padding: 0;
            position: relative;
            cursor: pointer;
            transition: color var(--transition-medium);

            &:hover {
                color: var(--color--text-strong);
            }
        }

        .bubble {
            position: relative;
            width: 1.25em;
            height: 1.25em;
            margin-right: 0.5em;
            transition:
                border-color var(--transition-medium),
                box-shadow var(--transition-medium);
            align-self: center;
            background-color: var(--color--form-bg);
            border: 1px solid var(--color--form-border);
            display: inline-block;
            flex-shrink: 0;
            border-radius: 50%;

            .c-label--radio:hover & {
                border-color: var(--color--form-border-hover);
            }

            .c-label--input:focus + .c-label--radio & {
                border-color: var(--color--link-text);
                box-shadow: 0 0 0 1px var(--color--link-text);
                z-index: 3;
            }
            &::after {
                /* dot */
                content: "";
                opacity: 0;
                position: absolute;
                inset: 0;
                margin: auto;
                width: 0.625em;
                aspect-ratio: 1;
                background-color: var(--color--link-text);
                border-radius: 50%;
                transform: scale(0.5);
                transition:
                    transform var(--transition-medium),
                    opacity var(--transition-medium);
            }
            .c-label--input:checked + .c-label--radio &::after {
                opacity: 1;
                transform: none;
            }
        }
    `,
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => S25RadioComponent), multi: true }],
})
export class S25RadioComponent<T> extends BaseControlValueAccessor<T> implements FormValueControl<T | null> {
    readonly disabled = input<boolean>(false);
    readonly value = model<T | null>(null);
    readonly radioValue = input.required<T>();
    readonly ariaLabel = input<string>("");
    /** HTML radio group name; use instead of [name] when the host also binds [field] (NG8022). */
    readonly radioGroupName = input<string>("");
    readonly name = input<string>("");

    readonly id = signal<number>(0);
    static count = 0;

    constructor() {
        super();
        S25RadioComponent.count++;
        this.id.set(S25RadioComponent.count);
    }

    onChange(val: T) {
        this.onValChange(val);
        this.onTouched();
    }

    onWriteValue(val: T) {
        this.value.set(val);
    }
}
